import React, { useEffect, useMemo, useState } from 'react'
import {
	Avatar,
	Box,
	Collapse,
	Dialog,
	IconButton,
	MenuItem,
	Tab,
	Tabs,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import TableRowsIcon from '@mui/icons-material/TableRows'
import ViewHeadlineIcon from '@mui/icons-material/ViewHeadline'
import { type GridColDef } from '@mui/x-data-grid-pro'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import dayjs, { Dayjs } from 'dayjs'
import { useModal } from '../../../../../store/ModalProvider'
import CustomButton from '../../CustomButton/CustomButton'
import { AnimatedDataGridPro } from '../../CustomDataGrid'
import { dataGridDensityTransitionSx } from '../../CustomDataGrid/dataGridDensityTransitionSx'
import CustomColumnMenu, { columnMenuPopperSx } from '../../CustomDataGrid/CustomColumnMenu'
import { popupActionButtonSx, popupSecondaryButtonSx } from '../popupButtonStyles'
import { calendarInputSx, calendarPopperSlotProps, popupInputLabelSx } from '../popupInputStyles'
import type { ActivityDeclarationsModalData } from '../../../../../types/modal'

type ActivityRow = ActivityDeclarationsModalData['toIssue'][number]

const dialogShadow =
	'0px 9px 46px 8px rgba(9, 32, 86, 0.12), 0px 24px 38px 3px rgba(9, 32, 86, 0.14), 0px 11px 15px -7px rgba(9, 32, 86, 0.2)'

// Invisible but clickable opener so picker still opens when clicked
const EmptyButton: React.ComponentType<any> = (props: any) => (
	<IconButton
		{...props}
		sx={{ ...(props?.sx || {}), opacity: 0 }}
	/>
)

const toIssueColumns = [
	{ key: 'motorista', label: 'Motorista', width: '200px' },
	{ key: 'nr', label: 'Nr', width: '40px' },
	{ key: 'idFilial', label: 'ID Filial', width: '60px' },
	{ key: 'filial', label: 'Filial', width: '60px' },
	{ key: 'idZona', label: 'ID Zona', width: '60px' },
	{ key: 'email', label: 'Email', width: '170px' },
	{ key: 'dataInicio', label: 'Data Início', width: '110px' },
	{ key: 'dataFim', label: 'Data Fim', width: '110px' },
	{ key: 'motivo', label: 'Motivo', width: '150px' },
	{ key: 'observacoes', label: 'Observações', width: '90px' },
]

const issuedColumns = [
	{ key: 'motorista', label: 'Motorista', width: '210px' },
	{ key: 'nr', label: 'Nr', width: '50px' },
	{ key: 'dataEmissao', label: 'Data Emissão', width: '140px' },
	{ key: 'dataEscala', label: 'Data Escala', width: '140px' },
	{ key: 'imprimiu', label: 'Imprimiu', width: '80px' },
	{ key: 'envEmail', label: 'Env Email', width: '90px' },
	{ key: 'exportou', label: 'Exportou', width: '90px' },
	{ key: 'dataInicio', label: 'Data Início', width: '140px' },
	{ key: 'dataFim', label: 'Data Fim', width: '140px' },
	{ key: 'emitidoPor', label: 'Emitido por', width: '120px' },
	{ key: 'motivo', label: 'M', width: '80px' },
]

const defaultToIssueRows: ActivityRow[] = [
	{
		id: 'declaration-1',
		motorista: 'Alexandre Gomes',
		nr: '0137',
		idFilial: '1',
		filial: 'Transol',
		idZona: '1',
		email: 'alexandre@email.com',
		dataInicio: '02/05/2024 - 08:00',
		dataFim: '03/05/2024 - 08:00',
		motivo: 'Período Descanso',
	},
	{
		id: 'declaration-2',
		motorista: 'Alexandre Gomes',
		nr: '0137',
		idFilial: '1',
		filial: 'Transol',
		idZona: '1',
		email: 'alexandre@email.com',
		dataInicio: '02/05/2024 - 08:00',
		dataFim: '03/05/2024 - 08:00',
		motivo: 'Período Descanso',
	},
	{
		id: 'declaration-3',
		motorista: 'Alexandre Gomes',
		nr: '0137',
		idFilial: '1',
		filial: 'Transol',
		idZona: '1',
		email: 'alexandre@email.com',
		dataInicio: '02/05/2024 - 08:00',
		dataFim: '03/05/2024 - 08:00',
		motivo: 'Período Descanso',
	},
]

const defaultIssuedRows: ActivityRow[] = [
	{
		id: 'issued-1',
		motorista: 'Alexandre Gomes',
		nr: '0137',
		idFilial: '1',
		filial: 'Transol',
		idZona: '1',
		email: 'alexandre@email.com',
		dataEmissao: '02/05/2024 - 08:00',
		dataEscala: '02/05/2024 - 08:00',
		imprimiu: 'Sim',
		envEmail: 'Sim',
		exportou: 'Sim',
		dataInicio: '02/05/2024 - 08:00',
		dataFim: '03/05/2024 - 08:00',
		emitidoPor: 'Jorge Santos',
		motivo: 'Período',
	},
	{
		id: 'issued-2',
		motorista: 'Alexandre Gomes',
		nr: '0137',
		idFilial: '1',
		filial: 'Transol',
		idZona: '1',
		email: 'alexandre@email.com',
		dataEmissao: '02/05/2024 - 08:00',
		dataEscala: '02/05/2024 - 08:00',
		imprimiu: 'Sim',
		envEmail: 'Sim',
		exportou: 'Sim',
		dataInicio: '02/05/2024 - 08:00',
		dataFim: '03/05/2024 - 08:00',
		emitidoPor: 'Jorge Santos',
		motivo: 'Período',
	},
	{
		id: 'issued-3',
		motorista: 'Alexandre Gomes',
		nr: '0137',
		idFilial: '1',
		filial: 'Transol',
		idZona: '1',
		email: 'alexandre@email.com',
		dataEmissao: '02/05/2024 - 08:00',
		dataEscala: '02/05/2024 - 08:00',
		imprimiu: 'Sim',
		envEmail: 'Sim',
		exportou: 'Sim',
		dataInicio: '02/05/2024 - 08:00',
		dataFim: '03/05/2024 - 08:00',
		emitidoPor: 'Jorge Santos',
		motivo: 'Período',
	},
]

const headerTextSx = {
	fontSize: '12px',
	fontWeight: 600,
	color: '#8695AF',
	letterSpacing: '0.17px',
	lineHeight: '24px',
	fontFamily: 'Poppins, sans-serif',
}

const cellTextSx = {
	fontSize: '12px',
	color: '#092056',
	lineHeight: 1.3,
	fontFamily: 'Poppins, sans-serif',
}

const motivoTextSx = {
	...cellTextSx,
	fontWeight: 500,
	letterSpacing: '0.15px',
	lineHeight: '24px',
}

const tableSx = {
	border: 'none',
	'--DataGrid-rowBorderColor': 'transparent',
	'& .MuiDataGrid-columnHeaders': {
		backgroundColor: '#FFFFFF',
		borderBottom: '1px solid rgba(134, 149, 175, 0.15)',
		borderTop: 'none',
		boxShadow: 'none',
	},
	'& .MuiDataGrid-columnHeaderTitle': {
		...headerTextSx,
	},
	'& .MuiDataGrid-columnSeparator': {
		opacity: 0,
		color: 'rgba(134, 149, 175, 0.6)',
		transition: 'opacity 0.2s ease',
	},
	'& .MuiDataGrid-columnHeader:hover .MuiDataGrid-columnSeparator': {
		opacity: 1,
	},
	'& .MuiDataGrid-columnSeparator--resizable': {
		cursor: 'col-resize',
	},
	'& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell': {
		borderRight: 'none',
	},
	'& .MuiDataGrid-cell': {
		...cellTextSx,
		borderBottom: 'none',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
	'& .MuiDataGrid-cell.motivo-cell': {
		...motivoTextSx,
	},
	'& .MuiDataGrid-row': {
		borderBottom: 'none',
	},
	'& .MuiDataGrid-row.activity-row-new': {
		backgroundColor: 'rgba(0, 165, 255, 0.08)',
		animation: 'activityRowPop 420ms ease',
	},
	'& .MuiDataGrid-row.activity-row-new:hover': {
		backgroundColor: 'rgba(0, 165, 255, 0.12)',
	},
	'& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus': {
		outline: 'none',
	},
	'@keyframes activityRowPop': {
		'0%': {
			opacity: 0.4,
			transform: 'translateY(-6px)',
		},
		'60%': {
			opacity: 1,
			transform: 'translateY(0)',
		},
		'100%': {
			opacity: 1,
			transform: 'translateY(0)',
		},
	},
}

const inputSx = {
	'& .MuiInputLabel-root': {
		...popupInputLabelSx,
	},
	'& .MuiOutlinedInput-root': {
		backgroundColor: '#FFFFFF',
		borderRadius: '4px',
		minHeight: '32px',
		fontSize: '12px',
		fontWeight: 500,
		color: '#092056',
		'& fieldset': {
			borderColor: 'rgba(134, 149, 175, 0.3)',
		},
		'&:hover fieldset': {
			borderColor: 'rgba(0, 165, 255, 0.4)',
		},
		'&.Mui-focused fieldset': {
			borderColor: 'rgba(0, 165, 255, 0.6)',
		},
	},
	'& .MuiOutlinedInput-input': {
		padding: '6px 10px',
		lineHeight: 1.3,
	},
	'& .MuiSelect-select': {
		padding: '6px 32px 6px 10px',
		display: 'flex',
		alignItems: 'center',
	},
	'& .MuiSelect-icon': {
		color: '#00A5FF',
		right: '10px',
	},
}

const CustomModalActivityDeclarations: React.FC = () => {
	const { isOpen, closeModal, modalData, modalHandlers } = useModal()
	const declarationsData = modalData as ActivityDeclarationsModalData | undefined
	const [handlePrint, handleSend] = modalHandlers ?? []
	const [tabValue, setTabValue] = useState(0)
	const [viewMode, setViewMode] = useState<'table' | 'list'>('table')
	const [showCreatePanel, setShowCreatePanel] = useState(true)
	const [selectedRowIds, setSelectedRowIds] = useState<string[]>([])
	const [lastAddedId, setLastAddedId] = useState<string | null>(null)
	const [createStartDate, setCreateStartDate] = useState<Dayjs | null>(dayjs('05/06/2024 09:00', 'DD/MM/YYYY HH:mm'))
	const [createEndDate, setCreateEndDate] = useState<Dayjs | null>(dayjs('05/06/2024 12:00', 'DD/MM/YYYY HH:mm'))
	const [createDriver, setCreateDriver] = useState('0109 - Agostinho Ferreira')
	const [createInterruption, setCreateInterruption] = useState('16 - Gozava de baixa ou de um período de repouso')
	const [issuedStartDate, setIssuedStartDate] = useState<Dayjs | null>(dayjs('02/05/2024', 'DD/MM/YYYY'))
	const [issuedEndDate, setIssuedEndDate] = useState<Dayjs | null>(dayjs('08/05/2025', 'DD/MM/YYYY'))
	const driverOptions = ['0109 - Agostinho Ferreira', '0137 - Alexandre Gomes', '0298 - Beatriz Silva']
	const interruptionOptions = [
		'16 - Gozava de baixa ou de um período de repouso',
		'15 - Não estava escalado para trabalhar',
		'14 - Período de descanso diário',
	]

	const [toIssueRows, setToIssueRows] = useState<ActivityRow[]>([])
	const [issuedRows, setIssuedRows] = useState<ActivityRow[]>([])

	useEffect(() => {
		if (!declarationsData) {
			return
		}

		const initialTab = declarationsData.initialTab === 'issued' ? 1 : 0
		setTabValue(initialTab)
		setShowCreatePanel(initialTab === 0)
		setToIssueRows(declarationsData.toIssue?.length ? declarationsData.toIssue : defaultToIssueRows)
		setIssuedRows(declarationsData.issued?.length ? declarationsData.issued : defaultIssuedRows)
	}, [declarationsData, isOpen])

	const rows = tabValue === 0 ? toIssueRows : issuedRows
	const isCreatePanelOpen = showCreatePanel && tabValue === 0
	const isIssuedTab = tabValue === 1
	const activeColumns = isIssuedTab ? issuedColumns : toIssueColumns
	const gridColumns = useMemo<GridColDef<ActivityRow>[]>(
		() =>
			activeColumns.map(column => {
				const width = Number.parseInt(column.width, 10)
				if (column.key === 'motorista') {
					return {
						field: column.key,
						headerName: column.label,
						width,
						renderCell: (params: { row: ActivityRow }) => {
							const initial = params.row.motorista?.[0] ?? 'M'
							return (
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										gap: '8px',
									}}
								>
									<Avatar
										src={params.row.avatarUrl}
										sx={{
											borderRadius: '100%',
											display: 'flex',
											flexDirection: 'column',
											alignItems: 'center',
											justifyContent: 'center',
											bgcolor: 'lightgrey 50% / cover no-repeat',
											width: 32,
											height: 32,
											fontSize: '20px',
											fontWeight: 400,
											lineHeight: 1,
										}}
									>
										{initial}
									</Avatar>
									<Typography sx={cellTextSx}>{params.row.motorista}</Typography>
								</Box>
							)
						},
					} as GridColDef<ActivityRow>
				}

				if (column.key === 'motivo') {
					return {
						field: column.key,
						headerName: column.label,
						width,
						cellClassName: 'motivo-cell',
					} as GridColDef<ActivityRow>
				}

				if (column.key === 'observacoes') {
					return {
						field: column.key,
						headerName: column.label,
						width,
						renderCell: () => (
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'flex-end',
									width: '100%',
								}}
							>
								<IconButton
									size='small'
									sx={{ padding: '5px', borderRadius: '999px' }}
								>
									<KeyboardArrowDownIcon sx={{ fontSize: 18, color: '#00A5FF' }} />
								</IconButton>
							</Box>
						),
					} as GridColDef<ActivityRow>
				}

				return {
					field: column.key,
					headerName: column.label,
					width,
				} as GridColDef<ActivityRow>
			}),
		[activeColumns],
	)
	const rowHeight = 32
	const gridHeight = Math.max(Math.min(240, rows.length * rowHeight) + 40, 160)

	useEffect(() => {
		setSelectedRowIds([])
	}, [tabValue, rows.length])

	useEffect(() => {
		if (!lastAddedId) {
			return
		}
		const timer = setTimeout(() => setLastAddedId(null), 900)
		return () => clearTimeout(timer)
	}, [lastAddedId])

	if (!declarationsData) {
		return null
	}

	const handleCreateDeclaration = () => {
		setShowCreatePanel(prev => !prev)
	}

	const handleCreateCancel = () => {
		setCreateStartDate(dayjs('05/06/2024 09:00', 'DD/MM/YYYY HH:mm'))
		setCreateEndDate(dayjs('05/06/2024 12:00', 'DD/MM/YYYY HH:mm'))
		setCreateDriver(driverOptions[0])
		setCreateInterruption(interruptionOptions[0])
		setShowCreatePanel(false)
	}

	const handleIssuedFiltersReset = () => {
		setIssuedStartDate(dayjs('02/05/2024', 'DD/MM/YYYY'))
		setIssuedEndDate(dayjs('08/05/2025', 'DD/MM/YYYY'))
	}

	const handleCreateAdd = () => {
		if (!createStartDate || !createEndDate) {
			return
		}

		const [driverNumber, ...driverNameParts] = createDriver.split(' - ')
		const driverName = driverNameParts.join(' - ').trim() || createDriver
		const emailPrefix = driverName.split(' ')[0]?.toLowerCase() || 'motorista'
		const newRow: ActivityRow = {
			id: `declaration-${Date.now()}`,
			motorista: driverName,
			nr: driverNumber?.trim() || '',
			idFilial: '1',
			filial: 'Transol',
			idZona: '1',
			email: `${emailPrefix}@email.com`,
			dataInicio: createStartDate.format('DD/MM/YYYY - HH:mm'),
			dataFim: createEndDate.format('DD/MM/YYYY - HH:mm'),
			motivo: createInterruption,
		}

		setToIssueRows(prev => [newRow, ...prev])
		setSelectedRowIds([])
		setLastAddedId(newRow.id)
		handleCreateCancel()
	}

	const handlePrintDeclarations = () => {
		if (typeof handlePrint === 'function') {
			handlePrint(rows)
			return
		}
		console.log('Imprimir declarações', rows)
	}

	const handleSendDeclarations = () => {
		if (typeof handleSend === 'function') {
			handleSend(rows)
			return
		}
		console.log('Enviar declarações', rows)
	}

	const pageSize = 10
	const pageLabel = rows.length > 0 ? `1-${rows.length} de ${rows.length}` : '0-0 de 0'
	const countLabel = tabValue === 0 ? 'A Emitir' : 'Emitidas'

	return (
		<Dialog
			open={isOpen}
			onClose={closeModal}
			maxWidth={false}
			sx={{
				pointerEvents: 'auto',
				zIndex: 2000,
			}}
			PaperProps={{
				sx: {
					width: 'min(1200px, 96vw)',
					borderRadius: '6px',
					backgroundColor: '#FFFFFF',
					boxShadow: dialogShadow,
					overflow: 'hidden',
					zIndex: 2001,
				},
			}}
		>
			<Box sx={{ fontFamily: 'Poppins, sans-serif', color: '#092056' }}>
				<Box
					sx={{
						padding: '16px 24px 0',
						display: 'flex',
						flexDirection: 'column',
						gap: '12px',
					}}
				>
					<Typography
						sx={{
							fontSize: '16px',
							fontWeight: 600,
							lineHeight: 1.75,
							letterSpacing: '0.15px',
							color: '#092056',
						}}
					>
						Declarações de Atividade
					</Typography>
					<Box sx={{ display: 'flex', justifyContent: 'center', paddingTop: '8px' }}>
						<Tabs
							value={tabValue}
							onChange={(_, value) => setTabValue(value)}
							TabIndicatorProps={{
								style: {
									backgroundColor: '#00A5FF',
									height: '3px',
									borderRadius: '2px',
								},
							}}
							sx={{
								minHeight: '32px',
								'& .MuiTabs-flexContainer': {
									gap: '24px',
								},
								'& .MuiTab-root': {
									fontSize: '14px',
									fontWeight: 600,
									textTransform: 'none',
									minHeight: '32px',
									padding: '6px 4px',
									color: '#8695AF',
								},
								'& .MuiTab-root.Mui-selected': {
									color: '#00A5FF',
								},
							}}
						>
							<Tab label='A Emitir' />
							<Tab label='Emitidas' />
						</Tabs>
					</Box>
				</Box>

				<Box
					sx={{
						padding: '16px 24px 24px',
						display: 'flex',
						flexDirection: 'column',
						gap: '24px',
					}}
				>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
						{isIssuedTab && (
							<Box
								sx={{
									display: 'flex',
									flexWrap: 'wrap',
									alignItems: 'center',
									justifyContent: 'space-between',
									gap: '16px',
								}}
							>
								<Box
									sx={{
										display: 'flex',
										flexWrap: 'wrap',
										alignItems: 'center',
										gap: '16px',
									}}
								>
									<LocalizationProvider dateAdapter={AdapterDayjs}>
										<DatePicker
											label='Data Inicial'
											value={issuedStartDate}
											onChange={newValue => setIssuedStartDate(newValue)}
											format='DD/MM/YYYY'
											slotProps={{
												textField: {
													size: 'small',
													sx: [inputSx, calendarInputSx, { minWidth: '150px' }],
												},
												...calendarPopperSlotProps,
											}}
											slots={{ openPickerButton: EmptyButton }}
										/>
									</LocalizationProvider>
									<Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#092056' }}>
										{String.fromCharCode(0x2014)}
									</Typography>
									<LocalizationProvider dateAdapter={AdapterDayjs}>
										<DatePicker
											label='Data Final'
											value={issuedEndDate}
											onChange={newValue => setIssuedEndDate(newValue)}
											format='DD/MM/YYYY'
											slotProps={{
												textField: {
													size: 'small',
													sx: [inputSx, calendarInputSx, { minWidth: '150px' }],
												},
												...calendarPopperSlotProps,
											}}
											slots={{ openPickerButton: EmptyButton }}
										/>
									</LocalizationProvider>
								</Box>
								<CustomButton
									variant='text'
									size='small'
									onClick={handleIssuedFiltersReset}
									sx={{
										color: '#00A5FF',
										fontWeight: 600,
										textTransform: 'none',
										padding: '4px 6px',
									}}
								>
									Limpar Filtros
								</CustomButton>
							</Box>
						)}
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
							}}
						>
							<Typography
								sx={{
									fontSize: '24px',
									fontWeight: 600,
									lineHeight: 1.235,
									color: '#092056',
								}}
							>
								{rows.length} {countLabel}
							</Typography>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
								{!isIssuedTab && (
									<>
										<CustomButton
											variant='text'
											size='small'
											onClick={handleCreateDeclaration}
											endIcon={
												<AddRoundedIcon
													sx={{
														fontSize: 16,
														transition: 'transform 200ms ease',
														transform: isCreatePanelOpen ? 'rotate(45deg)' : 'rotate(0deg)',
													}}
												/>
											}
											sx={{
												color: '#00A5FF',
												fontWeight: 600,
												fontSize: '14px',
												padding: '4px 2px',
												minWidth: 'auto',
												textTransform: 'none',
												'&:hover': {
													backgroundColor: 'transparent',
												},
											}}
										>
											Criar Nova Declaração
										</CustomButton>
										<Box
											sx={{
												width: '1px',
												height: '18px',
												backgroundColor: 'rgba(134, 149, 175, 0.3)',
											}}
										/>
									</>
								)}
								<ToggleButtonGroup
									size='small'
									exclusive
									value={viewMode}
									onChange={(_, nextValue) => {
										if (nextValue) {
											setViewMode(nextValue)
										}
									}}
									sx={{
										border: '1px solid rgba(134, 149, 175, 0.15)',
										borderRadius: '4px',
										'& .MuiToggleButton-root': {
											border: 'none',
											borderRadius: 0,
											padding: '4.8px',
											color: 'rgba(134, 149, 175, 1)',
											minWidth: '24px',
											'&:not(:last-of-type)': {
												borderRight: '0.6px solid rgba(134, 149, 175, 0.15)',
											},
										},
										'& .MuiToggleButton-root.Mui-selected': {
											backgroundColor: '#F5F7FC',
											color: '#00A5FF',
										},
									}}
								>
									<ToggleButton
										value='table'
										aria-label='table view'
									>
										<TableRowsIcon
											sx={{
												fontSize: 14,
												color: viewMode === 'table' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
											}}
										/>
									</ToggleButton>
									<ToggleButton
										value='list'
										aria-label='list view'
									>
										<ViewHeadlineIcon
											sx={{
												fontSize: 14,
												color: viewMode === 'list' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
											}}
										/>
									</ToggleButton>
								</ToggleButtonGroup>
							</Box>
						</Box>

						<Collapse
							in={isCreatePanelOpen}
							timeout={260}
							sx={{
								'& .MuiCollapse-wrapperInner': {
									transition: 'opacity 220ms ease, transform 220ms ease',
								},
							}}
						>
							<Box
								sx={{
									backgroundColor: '#F5F7FC',
									borderRadius: '6px',
									padding: '16px',
									display: 'flex',
									flexDirection: 'column',
									gap: '12px',
									opacity: isCreatePanelOpen ? 1 : 0,
									transform: isCreatePanelOpen ? 'translateY(0)' : 'translateY(-6px)',
									pointerEvents: isCreatePanelOpen ? 'auto' : 'none',
									transition: 'opacity 220ms ease, transform 220ms ease',
								}}
							>
								<Typography
									sx={{
										fontSize: '16px',
										fontWeight: 600,
										lineHeight: 1.6,
										color: '#092056',
									}}
								>
									Criar Nova Declaração
								</Typography>
								<Box
									sx={{
										display: 'flex',
										flexWrap: 'wrap',
										alignItems: 'center',
										gap: '12px',
									}}
								>
									<LocalizationProvider dateAdapter={AdapterDayjs}>
										<DateTimePicker
											label='Data Inicial'
											value={createStartDate}
											onChange={newValue => setCreateStartDate(newValue)}
											format='DD/MM/YYYY HH:mm'
											slotProps={{
												textField: {
													size: 'small',
													sx: [inputSx, calendarInputSx, { minWidth: '140px' }],
												},
												...calendarPopperSlotProps,
											}}
											slots={{ openPickerButton: EmptyButton }}
										/>
									</LocalizationProvider>
									<Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#092056' }}>
										{String.fromCharCode(0x2014)}
									</Typography>
									<LocalizationProvider dateAdapter={AdapterDayjs}>
										<DateTimePicker
											label='Data Final'
											value={createEndDate}
											onChange={newValue => setCreateEndDate(newValue)}
											format='DD/MM/YYYY HH:mm'
											slotProps={{
												textField: {
													size: 'small',
													sx: [inputSx, calendarInputSx, { minWidth: '140px' }],
												},
												...calendarPopperSlotProps,
											}}
											slots={{ openPickerButton: EmptyButton }}
										/>
									</LocalizationProvider>
									<TextField
										select
										label='Motorista'
										value={createDriver}
										onChange={event => setCreateDriver(event.target.value)}
										size='small'
										sx={{ ...inputSx, minWidth: '180px' }}
										SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
									>
										{driverOptions.map(option => (
											<MenuItem
												key={option}
												value={option}
											>
												{option}
											</MenuItem>
										))}
									</TextField>
									<TextField
										select
										label='Tipo de Interrupção'
										value={createInterruption}
										onChange={event => setCreateInterruption(event.target.value)}
										size='small'
										sx={{ ...inputSx, minWidth: '300px', flexGrow: 1 }}
										SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
									>
										{interruptionOptions.map(option => (
											<MenuItem
												key={option}
												value={option}
											>
												{option}
											</MenuItem>
										))}
									</TextField>
								</Box>
								<Box
									sx={{
										display: 'flex',
										justifyContent: 'flex-end',
										gap: '16px',
									}}
								>
									<CustomButton
										variant='text'
										size='small'
										onClick={handleCreateCancel}
										sx={{
											color: '#00A5FF',
											fontWeight: 600,
											textTransform: 'none',
											padding: '4px 6px',
										}}
									>
										Cancelar
									</CustomButton>
									<CustomButton
										variant='text'
										size='small'
										onClick={handleCreateAdd}
										sx={{
											color: '#00A5FF',
											fontWeight: 600,
											textTransform: 'none',
											padding: '4px 6px',
										}}
									>
										Adicionar
									</CustomButton>
								</Box>
							</Box>
						</Collapse>

						<Box
							sx={{
								borderRadius: '4px',
								backgroundColor: '#FFFFFF',
							}}
						>
							<Box sx={{ height: gridHeight }}>
								<AnimatedDataGridPro
									slots={{ columnMenu: CustomColumnMenu }}
									slotProps={{ basePopper: { sx: columnMenuPopperSx } }}
									rows={rows}
									columns={gridColumns}
									density='compact'
									rowHeight={rowHeight}
									columnHeaderHeight={40}
									checkboxSelection
									rowSelectionModel={selectedRowIds}
									onRowSelectionModelChange={newSelection => {
										setSelectedRowIds(newSelection as string[])
									}}
									disableRowSelectionOnClick
									hideFooter
									getRowClassName={params => (params.id === lastAddedId ? 'activity-row-new' : '')}
									sx={[tableSx, dataGridDensityTransitionSx]}
								/>
							</Box>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'flex-end',
									gap: '26px',
									padding: '2px 0',
								}}
							>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
									<Typography
										sx={{
											fontSize: '11px',
											fontWeight: 400,
											color: '#8695AF',
											lineHeight: 1.3,
										}}
									>
										Linhas por página
									</Typography>
									<Box sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
										<Typography
											sx={{
												fontSize: '11px',
												fontWeight: 400,
												color: '#092056',
												lineHeight: 1.3,
											}}
										>
											{pageSize}
										</Typography>
										<KeyboardArrowDownIcon sx={{ fontSize: 20, color: '#00A5FF' }} />
									</Box>
								</Box>
								<Typography
									sx={{
										fontSize: '11px',
										fontWeight: 400,
										color: '#092056',
										lineHeight: 1.3,
									}}
								>
									{pageLabel}
								</Typography>
								<Box sx={{ display: 'flex', alignItems: 'center' }}>
									<IconButton
										size='small'
										sx={{ padding: '8px' }}
									>
										<ChevronLeftRoundedIcon sx={{ fontSize: 20, color: '#00A5FF' }} />
									</IconButton>
									<IconButton
										size='small'
										sx={{ padding: '8px' }}
									>
										<ChevronRightRoundedIcon sx={{ fontSize: 20, color: '#00A5FF' }} />
									</IconButton>
								</Box>
							</Box>
						</Box>
					</Box>
				</Box>

				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'flex-end',
						gap: '8px',
						padding: '8px 24px 16px',
					}}
				>
					<CustomButton
						variant='text'
						onClick={closeModal}
						sx={popupSecondaryButtonSx}
					>
						Fechar
					</CustomButton>
					<CustomButton
						variant='text'
						onClick={handlePrintDeclarations}
						sx={popupSecondaryButtonSx}
					>
						Imprimir
					</CustomButton>
					<CustomButton
						variant='contained'
						color='primary'
						onClick={handleSendDeclarations}
						sx={popupActionButtonSx}
					>
						Enviar
					</CustomButton>
				</Box>
			</Box>
		</Dialog>
	)
}

export default CustomModalActivityDeclarations
